import moment, { Moment } from 'moment';
import { useCallback, useState } from 'react';

interface IWaveSpectraDateRange {
  key: string;
  start: Moment;
  end: Moment;
}

const MAX_RANGE_MONTHS = 1;
const DEFAULT_RANGE_MONTHS = 1;

export const useWaveSpectraDateRange = (datasetDateRange: [Moment, Moment] | null) => {
  const [localRange, setLocalRange] = useState<IWaveSpectraDateRange | null>(null);

  const rangeKey = datasetDateRange
    ? `${datasetDateRange[0]?.valueOf()}|${datasetDateRange[1]?.valueOf()}`
    : '';

  const hasLocalRange = localRange !== null && localRange.key === rangeKey;

  const getDefaultRange = useCallback((): [Moment, Moment] | null => {
    if (!datasetDateRange) return null;
    const defaultEndDate = moment(datasetDateRange[1]);
    const defaultStartDate = moment(datasetDateRange[1]).subtract(DEFAULT_RANGE_MONTHS, 'months');
    return [defaultStartDate, defaultEndDate];
  }, [datasetDateRange]);

  const defineDate = (rangeType: 'start' | 'end') => {
    const isStartDate = rangeType === 'start';

    if (hasLocalRange) return isStartDate ? localRange.start : localRange.end;
    const defaultRange = getDefaultRange();

    if (defaultRange) {
      return isStartDate ? defaultRange[0] : defaultRange[1];
    }
    return null;
  };

  const startDate = defineDate('start');
  const endDate = defineDate('end');

  const onStartDateChange = useCallback(
    (value: Moment | null) => {
      if (!value || !endDate) return;

      let newEndDate = endDate;
      if (value.isAfter(endDate)) {
        newEndDate = moment(value).add(DEFAULT_RANGE_MONTHS, 'months');
      } else if (value.isSame(endDate)) {
        newEndDate = value;
      }
      const maxEndDate = moment(value).add(MAX_RANGE_MONTHS, 'months');
      const finalEndDate = newEndDate.isAfter(maxEndDate) ? maxEndDate : newEndDate;

      setLocalRange({ key: rangeKey, start: value, end: finalEndDate });
    },
    [endDate, rangeKey]
  );

  const onEndDateChange = useCallback(
    (value: Moment | null) => {
      if (!value || !startDate) return;

      const newStartDate = value.isBefore(startDate) || value.isSame(startDate) ? value : startDate;
      const minStartDate = moment(value).subtract(MAX_RANGE_MONTHS, 'months');
      const finalStartDate = newStartDate.isBefore(minStartDate) ? minStartDate : newStartDate;

      setLocalRange({ key: rangeKey, start: finalStartDate, end: value });
    },
    [startDate, rangeKey]
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      if (datasetDateRange && current.isAfter(datasetDateRange[1], 'day')) return true;
      return datasetDateRange ? current.isBefore(datasetDateRange[0], 'day') : false;
    },
    [datasetDateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      if (startDate && current < startDate) return true;
      if (startDate && current.isAfter(moment(startDate).add(MAX_RANGE_MONTHS, 'months'), 'day'))
        return true;
      return datasetDateRange ? current.isAfter(datasetDateRange[1], 'day') : false;
    },
    [startDate, datasetDateRange]
  );

  return {
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    disabledStartDate,
    disabledEndDate,
  };
};
